import Arbor from '../../arbor'
import { ActivePage } from '../../enums/ActivePage'
import { BulkVisitorAnswerMetadataKey } from '../../enums/BulkVisitorAnswerMetadataKey'
import { Navigation } from '../../routing/Navigation'
import replaceNavigation from './replaceNavigation'
import State from './State'

let preloadCache
let webFormStarted = false

const cachedSteps = []

const WebFormController = new class {

    get cachedSteps() {
        return cachedSteps
    }

    ensureWebFormStartHook({ allowInlineRedirect = false } = {}) {

        if (webFormStarted) {
            return
        }

        if (!allowInlineRedirect && Arbor.store('qv').navigation.shouldInlineWithRedirect) {
            return
        }

        Arbor.store('qv').runOnWebFormStartHook()
        webFormStarted = true

    }

    hasServerRenderedWebFormContent() {

        const navigation = Arbor.store('qv').navigation

        if (navigation.activePage !== ActivePage.WebFormChat) {
            return false
        }

        if (navigation.hasInlineWebForm || navigation.shouldInlineWithRedirect) {
            return false
        }

        const mainContent = document.querySelector('[qv-ref="mainContent"]')

        return Boolean(mainContent?.innerHTML.trim() && mainContent.querySelector('[x-title="web-form"]'))

    }

    async syncStepFromRouter() {

        const routerStep = parseInt(Arbor.$router.params.step, 10)

        if (!Number.isNaN(routerStep) && routerStep > 0) {

            State.step = routerStep

            if (usesFlexibleJourney()) {
                await this.syncFlexiblePageIndex(routerStep - 1)
            }

            return

        }

        if (State.step === 0) {
            State.incrementStep()
        }

    }

    async preload() {

        const navigation = Arbor.store('qv').navigation

        if (!navigation.hasGroups) {
            return true
        }

        if (typeof preloadCache !== 'undefined') {
            return true
        }

        if (navigation.activePage === ActivePage.WebFormChat) {

            const mainContentElement = document.querySelector('[qv-ref="mainContent"]')

            if (mainContentElement && mainContentElement.innerHTML.trim()) {
                preloadCache = mainContentElement.innerHTML
            }

            return true

        }

        preloadCache = await fetch(appendQuery(`/fast/web-form/${ Arbor.store('state').visitorId }`))
            .then(response => response.ok ? response.json() : Promise.reject(response))
            .then(data => data.data.view)

        return true

    }

    async start() {

        if (!Arbor.store('qv').navigation.hasGroups) {

            this.endForm()

            return

        }

        await this.preload()
        this.ensureWebFormStartHook()

        if (this.hasServerRenderedWebFormContent()) {

            await this.syncStepFromRouter()

            if (typeof preloadCache !== 'undefined') {
                cachedSteps[ State.step ] = preloadCache
            }

            return

        }

        /**
         * If this is an inline web form and start() is called again
         * it means the visitor clicked on a start web form cta
         */
        if (State.step === 1 && (Arbor.store('qv').navigation.hasInlineWebForm || Arbor.store('qv').navigation.shouldInlineWithRedirect)) {

            showFirstStep()

            return

        }

        /*
         * Only increment step if we're starting from step 0 (landing page -> web form)
         * If we're already on step 1, it means we loaded directly on step 1 from URL
         */
        if (State.step === 0) {
            State.incrementStep()
        }

        if (Arbor.store('qv').navigation.hasInlineWebForm || Arbor.store('qv').navigation.shouldInlineWithRedirect) {
            return
        }

        showFirstStep()

    }

    async submitAnswer(offset, form) {

        State.canSubmit = false
        State.formErrors = {}
        State.loading = true

        if (Arbor.store('qv').navigation.shouldInlineWithRedirect && Arbor.store('qv').navigation.startWebFormInNewTabUrl) {

            Arbor.store('qv').navigation.openNewTabWithQueryString(form)
            State.reset()

            return

        }

        Arbor.store('qv').runBeforeStepSubmissionHook(form)

        const response = await fetch(appendQuery(`/fast/web-form/${ Arbor.store('state').visitorId }/${ offset }`), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(form),
        })

        const data = await response.json()

        if (data.success === false) {

            State.formErrors = data.error.errors
            State.loading = false
            State.canSubmit = true

            return

        }

        if (!Arbor.store('qv').navigation.hasInlineWebForm && Arbor.store('qv').navigation.activePage !== ActivePage.WebFormChat) {
            Arbor.store('qv').navigation.activePage = ActivePage.WebFormChat
        }

        this.ensureWebFormStartHook({ allowInlineRedirect: true })
        Arbor.store('qv').runAfterStepSubmissionHook(form)
        Arbor.store('qv').stepHistory.storeAnsweredStepOffset(offset)

        // Should redirect
        if (data.code === 2004) {

            Arbor.store('qv').navigation.redirectTo(data.url)

            return

        }

        // Should redirect via modal
        if (data.code === 2005) {

            Arbor.store('qv').modal.openModalFromRaw(data.modal, { canClose: false })

            return

        }

        // No more groups
        if (data.code === 2001) {

            Arbor.store('qv').runBeforeFormSubmissionHook(form)
            Arbor.store('qv').runAfterFormSubmissionHook(form)
            this.endForm()

            return

        }

        State.webFormContent = data.data.view
        State.reset()
        State.incrementStep()
        cachedSteps[ State.step ] = State.webFormContent
        Arbor.store('qv').navigation.goToStep(State.step, replaceNavigation(State.step))

    }

    async submitLandingPageAnswers(form) {

        if (!Arbor.store('qv').webForm.component.hasFlexibleFormData()) {
            return true
        }

        State.canSubmit = false
        State.formErrors = {}
        State.loading = true
        this.ensureWebFormStartHook({ allowInlineRedirect: true })
        Arbor.store('qv').runBeforeStepSubmissionHook(form)

        const response = await fetch(appendQuery(`/fast/web-form/${ Arbor.store('state').visitorId }/bulk-answers`), {
            method: 'POST',
            headers: {
                accept: 'application/json',
                'Content-Type': 'application/json',
                'x-csrf-token': Arbor.store('qv').getCsrfToken(),
            },
            body: JSON.stringify(buildBulkAnswerPayload(form)),
        })

        const data = await response.json()

        if (data.success === false) {

            State.formErrors = data.error.errors
            State.loading = false
            State.canSubmit = true

            return false

        }

        Arbor.store('qv').runAfterStepSubmissionHook(form)

        for (const offset of Object.keys(form).filter(key => /^\d+:\d+$/.test(key))) {
            Arbor.store('qv').stepHistory.storeAnsweredStepOffset(offset)
        }

        State.reset()

        return true

    }

    async submitLandingPage(form) {

        const didPersistLandingPageAnswers = await this.submitLandingPageAnswers(form)

        if (!didPersistLandingPageAnswers) {
            return false
        }

        return Arbor.store('qv').navigation.start()

    }

    async submitFlexibleAnswer(form) {

        State.canSubmit = false
        State.formErrors = {}
        State.loading = true
        this.ensureWebFormStartHook({ allowInlineRedirect: true })
        Arbor.store('qv').runBeforeStepSubmissionHook(form)

        const response = await fetch(appendQuery(`/fast/web-form/${ Arbor.store('state').visitorId }/bulk-answers`), {
            method: 'POST',
            headers: {
                accept: 'application/json',
                'Content-Type': 'application/json',
                'x-csrf-token': Arbor.store('qv').getCsrfToken(),
            },
            body: JSON.stringify(buildBulkAnswerPayload(form)),
        })

        const data = await response.json()

        if (data.success === false) {

            State.formErrors = data.error.errors
            State.loading = false
            State.canSubmit = true

            return

        }

        Arbor.store('qv').runAfterStepSubmissionHook(form)

        for (const offset of Object.keys(form).filter(key => /^\d+:\d+$/.test(key))) {
            Arbor.store('qv').stepHistory.storeAnsweredStepOffset(offset)
        }

        await this.completeFlexiblePage()

    }

    async advanceFlexiblePage() {

        State.canSubmit = false
        State.formErrors = {}
        State.loading = true
        this.ensureWebFormStartHook({ allowInlineRedirect: true })
        await this.completeFlexiblePage()

    }

    async completeFlexiblePage() {

        const response = await fetch(appendQuery(`/fast/web-form/${ Arbor.store('state').visitorId }/flexible-page/complete`), {
            method: 'POST',
            headers: {
                accept: 'application/json',
                'Content-Type': 'application/json',
                'x-csrf-token': Arbor.store('qv').getCsrfToken(),
            },
            body: JSON.stringify({}),
        })

        const data = await response.json()

        if (data.success === false) {

            State.loading = false
            State.canSubmit = true

            return

        }

        if (data.code === 2001) {

            Arbor.store('qv').runBeforeFormSubmissionHook({})
            Arbor.store('qv').runAfterFormSubmissionHook({})
            Arbor.store('qv').thankYou.controller.start()

            return

        }

        State.webFormContent = data.data.view
        State.reset()
        State.incrementStep()
        cachedSteps[ State.step ] = State.webFormContent
        Arbor.store('qv').navigation.goToStep(State.step, replaceNavigation(State.step))

    }

    async syncFlexiblePageIndex(index) {

        if (!usesFlexibleJourney()) {
            return
        }

        await fetch(appendQuery(`/fast/web-form/${ Arbor.store('state').visitorId }/flexible-page/sync`), {
            method: 'POST',
            headers: {
                accept: 'application/json',
                'Content-Type': 'application/json',
                'x-csrf-token': Arbor.store('qv').getCsrfToken(),
            },
            body: JSON.stringify({ index }),
        })

    }

    async updateVisitorData(data) {

        await fetch(`/update-visitor-data/${ Arbor.store('state').visitorId }`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                'x-csrf-token': Arbor.store('qv').getCsrfToken(),
            },
            body: JSON.stringify(data),
        })

    }

    endForm() {

        if (Arbor.store('qv').navigation.bypassLoadingPage) {
            Arbor.store('qv').thankYou.controller.start()
        } else {
            Arbor.store('qv').loading.controller.start()
        }

    }

}()

function usesFlexibleJourney() {
    return Arbor.store('qv').webForm.state.usesFlexibleJourney
}

function appendQuery(path) {
    return Arbor.store('qv').helpers.appendQuery(path)
}

function showFirstStep() {

    Arbor.store('qv').navigation.goToStep(State.step, replaceNavigation(State.step))
    State.webFormContent = preloadCache
    Arbor.store('qv').navigation.activePage = ActivePage.WebFormChat
    cachedSteps[ State.step ] = preloadCache
    preloadCache = undefined

}

const BULK_ANSWER_METADATA_KEYS = Object.values(BulkVisitorAnswerMetadataKey)

function buildBulkAnswerPayload(form) {

    const payload = {}
    const metadata = {}

    for (const [ key, value ] of Object.entries(form)) {

        if (/^\d+:\d+$/.test(key)) {

            payload[ key ] = value

            continue

        }

        if (BULK_ANSWER_METADATA_KEYS.includes(key)) {
            metadata[ key ] = value
        }

    }

    if (Object.keys(metadata).length > 0) {
        payload.metadata = metadata
    }

    return payload

}

export default WebFormController
